import { useEffect, useState } from "react";

interface RoutingAttempt {
  sipUser: string;
  at: string;
  outcome: "ringing" | "answered" | "no_answer" | "busy" | "cancelled" | "failed";
}

interface CdrRow {
  id: string;
  callId: string;
  direction: "inbound" | "outbound";
  agentEmail: string | null;
  agentName: string | null;
  fromUri: string;
  toUri: string;
  startedAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  durationMs: number | null;
  endCause: string | null;
  recordingPath: string | null;
  /** Ring journey (inbound): every agent rung, in order, with the leg
   *  outcome. Null on legacy rows and no-agent calls. */
  routing: RoutingAttempt[] | null;
}

export function History(props: { token: string }) {
  const [rows, setRows] = useState<CdrRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/cdr?limit=50", {
          headers: { authorization: `Bearer ${props.token}` },
        });
        if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
        const data = (await res.json()) as { rows: CdrRow[] };
        if (!cancelled) setRows(data.rows);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.token, refreshKey]);

  return (
    <section className="card">
      <div className="card-header">
        <h3>Historique des appels</h3>
        <div className="actions">
          <button className="ghost" onClick={() => setRefreshKey((k) => k + 1)}>
            ↻ Actualiser
          </button>
        </div>
      </div>
      {error && <div className="error">{error}</div>}
      {!error && rows == null && <div className="empty">Chargement…</div>}
      {rows != null && rows.length === 0 && (
        <div className="empty">Aucun appel — passez-en un puis actualisez.</div>
      )}
      {rows != null && rows.length > 0 && (
        <table className="data">
          <thead>
            <tr>
              <th>Date</th>
              <th>Direction</th>
              <th>Correspondant</th>
              <th>Routage / décroché par</th>
              <th>Durée</th>
              <th>Issue</th>
              <th>Enregistrement</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <RecordingRow key={r.id} row={r} token={props.token} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function RecordingRow(props: { row: CdrRow; token: string }) {
  const { row } = props;
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load(): Promise<void> {
    setError(null);
    try {
      const res = await fetch(`/api/cdr/${row.id}/recording`, {
        headers: { authorization: `Bearer ${props.token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setAudioUrl(URL.createObjectURL(await res.blob()));
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <tr>
      <td>{new Date(row.startedAt).toLocaleString("fr-FR")}</td>
      <td>{row.direction === "outbound" ? "↗ Sortant" : "↙ Entrant"}</td>
      <td><code>{row.direction === "outbound" ? row.toUri : row.fromUri}</code></td>
      <td><RoutingCell row={row} /></td>
      <td>{formatDuration(row.durationMs)}</td>
      <td>
        <span className={`pill ${dispoClass(row.endCause)}`}>{translateCause(row.endCause)}</span>
      </td>
      <td>
        {row.recordingPath == null && <span className="muted">—</span>}
        {row.recordingPath != null && audioUrl == null && !error && (
          <button className="ghost" onClick={load} type="button">
            ▶ Écouter
          </button>
        )}
        {audioUrl != null && (
          <audio src={audioUrl} controls style={{ height: "1.6rem", verticalAlign: "middle" }} />
        )}
        {error && <span className="error">{error}</span>}
      </td>
    </tr>
  );
}

/** Ring journey rendered as a chain: « lea ✗ → marc ✓ ». The agent
 *  who answered is green; everyone rung without answering is grey.
 *  Falls back to the answering agent's name for rows predating the
 *  routing capture, and to the calling agent for outbound calls. */
function RoutingCell(props: { row: CdrRow }) {
  const { row } = props;
  if (row.direction === "outbound") {
    return row.agentName || row.agentEmail ? (
      <span>par <strong>{row.agentName ?? row.agentEmail}</strong></span>
    ) : (
      <span className="muted">—</span>
    );
  }
  if (row.routing && row.routing.length > 0) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
        {row.routing.map((a, i) => (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {i > 0 && <span className="muted">→</span>}
            <span
              title={`${a.sipUser} : ${OUTCOME_FR[a.outcome] ?? a.outcome} (${new Date(a.at).toLocaleTimeString("fr-FR")})`}
              style={{
                fontWeight: a.outcome === "answered" ? 700 : 400,
                color: a.outcome === "answered" ? "var(--success)" : "var(--text-muted)",
                whiteSpace: "nowrap",
              }}
            >
              {a.sipUser} {OUTCOME_ICON[a.outcome] ?? ""}
            </span>
          </span>
        ))}
      </span>
    );
  }
  // Legacy rows: only the answerer (if any) was recorded.
  if (row.agentName || row.agentEmail) {
    return <span><strong>{row.agentName ?? row.agentEmail}</strong> ✓</span>;
  }
  return <span className="muted">personne sonné</span>;
}

const OUTCOME_ICON: Record<string, string> = {
  answered: "✓",
  no_answer: "✗",
  busy: "⊘",
  cancelled: "◌",
  failed: "!",
  ringing: "…",
};
const OUTCOME_FR: Record<string, string> = {
  answered: "a décroché",
  no_answer: "pas de réponse",
  busy: "occupé",
  cancelled: "annulé (appelant raccroché ou décroché ailleurs)",
  failed: "échec (injoignable)",
  ringing: "en sonnerie",
};

function formatDuration(ms: number | null): string {
  if (ms == null || ms <= 0) return "—";
  const sec = Math.round(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m} min ${String(s).padStart(2, "0")}` : `${s} s`;
}
function translateCause(cause: string | null): string {
  if (!cause) return "—";
  const c = cause.toUpperCase();
  if (c === "ANSWERED") return "Décroché";
  if (c === "NO ANSWER") return "Pas de réponse";
  if (c === "BUSY") return "Occupé";
  if (c === "FAILED") return "Échec";
  return cause;
}
function dispoClass(cause: string | null): string {
  if (!cause) return "idle";
  const c = cause.toUpperCase();
  if (c === "ANSWERED") return "registered";
  if (c === "NO ANSWER" || c === "BUSY") return "on-call";
  if (c === "FAILED") return "failed";
  return "idle";
}
